/**
 * La riga sottile in cima a ogni sezione di modulo.
 *
 * Sostituisce il blocco titolo (quadratino + nome grande): la barra dice già
 * dove sei, ripeterlo era spazio speso per niente (Enrico, 16-08). Resta la
 * data del dato, perché ogni numero delle pagine dipende da quella.
 *
 * ⚠️ **Dal 27-08-2026 non porta più nemmeno il nome della sezione**: con le tab
 * in cima a ogni modulo la pillola accesa lo dice già, e il nome ricompariva in
 * grassetto due centimetri sotto (Enrico: «la tab è evidente, è inutile mettere
 * il titolo sotto»). Qui resta ciò che la tab non può dire: **di quando è il
 * dato**, con quanti sono e cosa c'è da sapere.
 *
 * Nata in Commesse, promossa a componente condiviso quando è arrivata la
 * Pianificazione (17-08): due copie della stessa riga sarebbero tornate a
 * divergere, come è già successo alle due chat degli agenti.
 */
import { cn } from "@/lib/utils";

function formatDataIt(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatDataTimeIt(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Giorni trascorsi dal caricamento del dato: sopra la settimana lo diciamo. */
function giorniFa(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** Oltre un mese il dato non è «un po' vecchio», è di un'altra stagione: il
 *  badge passa da ambra a rosso. In Commesse non cambia niente (lo snapshot è di
 *  tre settimane); in Pianificazione il piano è di quattro mesi. */
const SOGLIA_AMBRA = 7;
const SOGLIA_ROSSA = 30;

export function RigaTesta({
  contesto,
  caricatoIl,
  etichettaData = "dati del",
  soloGiorno = false,
  titoloBadge,
  destra,
  children,
}: {
  /** Contesto breve in apertura di riga, es. «201 commesse». */
  contesto?: string;
  caricatoIl?: string | null;
  /** Come si chiama quella data: «dati del», «piano del», … */
  etichettaData?: string;
  /** Senza l'ora: un piano di produzione è di un giorno, non di un istante. */
  soloGiorno?: boolean;
  /** Tooltip del badge di vecchiaia; ogni modulo ha la sua ragione. */
  titoloBadge?: string;
  /** Nota a destra, allineata al bordo. */
  destra?: string;
  /** Azioni a destra (un pulsante, un menu). Vince su `destra`. */
  children?: React.ReactNode;
}) {
  const giorni = caricatoIl ? giorniFa(caricatoIl) : null;
  const grave = giorni !== null && giorni >= SOGLIA_ROSSA;

  return (
    <div
      // Ancoraggio per le prove in browser: cercare «N commesse» nel corpo intero
      // pescava numeri di altri riquadri e si è rotto al primo cambio di formato.
      data-riga="testa"
      className="mb-4 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 border-b pb-2.5"
    >
      {contesto && (
        <span className="text-[12px] font-medium text-foreground">{contesto}</span>
      )}
      {caricatoIl && (
        <>
          {contesto && <span className="text-muted-foreground/60">·</span>}
          <span className="text-[11.5px] text-muted-foreground">
            {etichettaData}{" "}
            <span className="text-foreground">
              {soloGiorno ? formatDataIt(caricatoIl) : formatDataTimeIt(caricatoIl)}
            </span>
          </span>
          {giorni !== null && giorni >= SOGLIA_AMBRA && (
            <span
              className={cn(
                "rounded-full border px-2 text-[10.5px]",
                grave
                  ? "border-red-300 bg-red-50 font-medium text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
                  : "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
              )}
              title={
                titoloBadge ??
                "Il dato viene da un caricamento manuale: più invecchia, meno i numeri descrivono oggi"
              }
            >
              {giorni} giorni fa
            </span>
          )}
        </>
      )}
      {children ? (
        <span className="ml-auto flex items-center gap-2">{children}</span>
      ) : (
        destra && <span className="ml-auto text-[11.5px] text-muted-foreground">{destra}</span>
      )}
    </div>
  );
}
